import './landing/landing.css';
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Logo from './common/Logo';
import SiteHeader from './layout/SiteHeader';
import { Helmet } from 'react-helmet-async';
import WaitlistModal from './WaitlistModal';
import HeroBeforeAfter from './landing/HeroBeforeAfter';
import UploadPrepModal from './landing/UploadPrepModal';
import { validateMediaFile } from '../utils/fileValidation';
import { getReferralCode, clearReferralCode, getCouponCode, clearCouponCode } from '../utils/attribution';
import api from '../utils/api';
import { usePricing } from '../hooks/usePricing';
import { trackEvent } from '../utils/analytics';
import { TEMPLATES } from '../utils/constants';
import SHOWCASE_WINDOWS from '../data/showcaseWindows.json';
import { motion as Motion, useReducedMotion } from 'framer-motion';
import { Gift, Ticket, Volume2, VolumeX } from 'lucide-react';
import toast from 'react-hot-toast';

/* ─── helpers ───────────────────────────────────────────── */
const Icon = ({ name, className = '' }) => (
  <span className={`ms-icon ${className}`}>{name}</span>
);

const Check = ({ children, color = 'text-indigo-600 dark:text-indigo-400' }) => (
  <li className="flex items-start gap-2.5 font-jakarta text-[15px] leading-snug">
    <Icon name="check" className={`text-[18px] shrink-0 mt-0.5 ${color}`} />
    {children}
  </li>
);

/* Shared type + surface styles, so every section speaks the same brand:
   Baloo 2 headlines, Jakarta body, indigo actions and marigold caption highlights. */
const H2 = 'landing-section-title font-brand font-extrabold text-stone-900 dark:text-stone-100 text-[2.5rem] sm:text-5xl lg:text-[3.5rem] leading-[1.05]';
const LEAD = 'font-jakarta text-lg text-stone-600 dark:text-stone-400 leading-relaxed';
const CARD = 'rounded-3xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900';
const BTN_PRIMARY = 'landing-primary cap-btn inline-flex items-center justify-center gap-2.5 rounded-2xl bg-indigo-600 text-white font-jakarta font-bold disabled:opacity-70';

/* ─── Header ─── shared with the auth pages, see layout/SiteHeader.jsx ─── */

const BrandHeadline = () => (
  <h1 className="landing-headline">
    Your voice.<br />
    Your vibe.<br />
    <span className="landing-highlight">Now captioned.</span>
  </h1>
);

/* ─── Hero ───────────────────────────────────────────────── */
/* A friend's invite link: confirm the code with the server before promising
   anything, and forget it if it is not real. Returns the bonus in minutes. */
const useReferralBonus = (signedIn) => {
  const [bonusMinutes, setBonusMinutes] = useState(0);
  useEffect(() => {
    // The bonus is for a new account; someone already signed in cannot claim it.
    if (signedIn) return undefined;
    const code = getReferralCode();
    if (!code) return undefined;
    let live = true;
    api.get(`/api/referral/${encodeURIComponent(code)}`)
      .then(({ data }) => {
        if (!live) return;
        if (data?.valid) {
          setBonusMinutes(Math.round((data.refereeSeconds || 0) / 60));
          // One event per code per browser, not one per visit for 30 days.
          try {
            const key = `dc_ref_seen_${code}`;
            if (!localStorage.getItem(key)) { localStorage.setItem(key, '1'); trackEvent('referral_landing', { code }); }
          } catch { /* analytics only */ }
        } else {
          clearReferralCode();
        }
      })
      .catch(() => { /* leave the stored code; the server will judge it at signup */ });
    return () => { live = false; };
  }, [signedIn]);
  return signedIn ? 0 : bonusMinutes;
};

/* A creator's or giveaway link (/?coupon=CODE): confirm the code is live
   before promising anything, and forget a dead one. Returns { code, minutes }
   or null. Shown to signed-out visitors only: a signed-in user gets the code
   applied on their dashboard instead (CouponAutoApply). */
const useCouponBonus = (signedIn) => {
  const [bonus, setBonus] = useState(null);
  useEffect(() => {
    if (signedIn) return undefined;
    const code = getCouponCode();
    if (!code) return undefined;
    let live = true;
    api.get(`/api/coupons/check/${encodeURIComponent(code)}`)
      .then(({ data }) => {
        if (!live) return;
        if (data?.valid) {
          setBonus({ code: data.code, minutes: data.minutes });
          try {
            const key = `dc_coupon_seen_${code}`;
            if (!localStorage.getItem(key)) { localStorage.setItem(key, '1'); trackEvent('coupon_landing', { code }); }
          } catch { /* analytics only */ }
        } else {
          clearCouponCode();
        }
      })
      .catch(() => { /* leave the stored code; the server will judge it at redemption */ });
    return () => { live = false; };
  }, [signedIn]);
  return signedIn ? null : bonus;
};

/* Headline + upload button on the left, raw phone → captioned phone on the
   right. The file picked here is NOT uploaded yet — it waits in the browser
   (pendingUpload) until the visitor signs up, then starts on its own. */
const Hero = () => {
  const inputRef = useRef(null);
  const [validating, setValidating] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [prep, setPrep] = useState(null); // { file, info: { sizeStr, durationStr } }
  const { user: signedInUser } = useAuth();
  const bonusMinutes = useReferralBonus(Boolean(signedInUser));
  const coupon = useCouponBonus(Boolean(signedInUser));

  const openPicker = () => {
    trackEvent('hero_upload_clicked');
    inputRef.current?.click();
  };

  const acceptFile = async (file) => {
    if (!file) return;
    setValidating(true);
    const result = await validateMediaFile(file);
    setValidating(false);
    if (result.error) {
      trackEvent('hero_file_invalid');
      toast.error(result.error);
      return;
    }
    trackEvent('hero_file_selected');
    setPrep({ file, info: { sizeStr: result.sizeStr, durationStr: result.durationStr } });
  };

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = null;
    acceptFile(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    trackEvent('hero_file_dropped');
    acceptFile(e.dataTransfer.files?.[0]);
  };

  /* Rendered twice: beside the headline on desktop, under the phones on
     mobile (so the eye path there stays phone → arrow → phone → button). */
  const cta = (align) => (
    <div className={`flex flex-col gap-3 ${align}`}>
      <button
        onClick={openPicker}
        disabled={validating}
        className={`${BTN_PRIMARY} w-full sm:w-auto min-h-16 px-9 text-[17px]`}
      >
        <Icon name={validating ? 'hourglass_top' : 'upload_file'} className="text-2xl" />
        {validating ? 'Checking your video...' : 'Caption my video - free'}
      </button>
      {bonusMinutes > 0 || coupon ? (
        <p className="font-jakarta text-sm text-stone-600 dark:text-stone-400">
          <span className="inline-flex flex-wrap gap-1.5">
            {bonusMinutes > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 px-2.5 py-1 text-indigo-700 dark:text-indigo-300 font-semibold">
                <Gift className="h-4 w-4 shrink-0" aria-hidden="true" />
                Your friend gave you {bonusMinutes} extra free minutes
              </span>
            )}
            {coupon && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 px-2.5 py-1 text-indigo-700 dark:text-indigo-300 font-semibold">
                <Ticket className="h-4 w-4 shrink-0" aria-hidden="true" />
                Code <span className="font-jetbrains tracking-wider">{coupon.code}</span>: {coupon.minutes} extra free minutes
              </span>
            )}
          </span>
          <span className="block mt-1.5">{2 + bonusMinutes + (coupon?.minutes || 0)} minutes free, no card needed.{coupon ? ' The code is applied after you sign up and verify your email.' : ''}</span>
        </p>
      ) : (
        <p className="font-jakarta text-sm text-stone-600 dark:text-stone-400">
          First 2 minutes free, no card needed.
        </p>
      )}
    </div>
  );

  return (
    <section
      id="top" className="landing-hero relative pt-28 lg:pt-36 pb-14 lg:pb-20"
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      {/* Drop overlay — the whole hero is a dropzone */}
      {dragActive && (
        <div className="absolute inset-4 z-40 border-4 border-dashed border-indigo-500 rounded-[2.5rem] bg-indigo-50/90 dark:bg-indigo-950/90 flex items-center justify-center pointer-events-none">
          <div className="flex flex-col items-center gap-3 text-indigo-600 dark:text-indigo-300">
            <Icon name="upload_file" className="text-6xl" />
            <p className="text-2xl font-brand font-extrabold">Drop your video here</p>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-[minmax(0,11fr)_minmax(0,13fr)] gap-10 lg:gap-8 items-center">
        <div className="flex flex-col gap-6 lg:gap-7">
          <a href="#new-styles" className="self-start -mb-2 flex flex-wrap items-center gap-x-2.5 gap-y-2 font-jakarta text-sm font-semibold text-stone-700 dark:text-stone-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
            <NewTag />
            <span>Editorial, Handwritten, Poster &amp; Typewriter</span>
            <Icon name="arrow_forward" className="text-base" />
          </a>
          <BrandHeadline />
          <p className="font-jakarta text-lg sm:text-xl text-stone-600 dark:text-stone-400 leading-relaxed max-w-md">
            Hindi and Hinglish videos, captioned in English letters and synced to every word.
          </p>
          <div className="hidden lg:block">{cta('items-start')}</div>
        </div>

        <HeroBeforeAfter cta={<div className="lg:hidden w-full">{cta('items-center text-center')}</div>} />
      </div>

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={handleChange}
        accept="audio/*,video/*"
      />
      {prep && <UploadPrepModal file={prep.file} info={prep.info} onClose={() => setPrep(null)} />}
    </section>
  );
};

/* ─── Caption tape: Hinglish one-liners set in real template styles, run as a
   strip of tape across the page. The product's own output is the ornament. ── */
const TAPE_CAPTIONS = [
  { text: 'KYA BAAT HAI!', style: { fontFamily: 'Bangers, cursive', color: '#FFD54F', letterSpacing: '1px' } },
  { text: 'SEEDHA POINT PE', style: { fontFamily: 'Oswald, sans-serif', color: '#8ABE4C', fontWeight: 700 } },
  { text: 'paisa vasool', style: { fontFamily: 'Montserrat, sans-serif', color: '#FFFFFF', fontWeight: 800 } },
  { text: 'SUNO SUNO', style: { fontFamily: 'Bangers, cursive', color: '#FF5252', letterSpacing: '1px' } },
  { text: 'ek number!', style: { fontFamily: 'Kalam, cursive', color: '#F7C95C', fontWeight: 700 } },
  { text: 'FULL VIBE', style: { fontFamily: 'Anton, sans-serif', color: '#A5B4FC', letterSpacing: '0.5px' } },
  { text: 'toh chaliye shuru karte hain', style: { fontFamily: 'Montserrat, sans-serif', color: '#FACC15', fontWeight: 800 } },
];

const Waveform = () => (
  <svg viewBox="0 0 16 16" className="w-4 h-4 shrink-0 fill-stone-600" aria-hidden="true">
    <rect x="2" y="5" width="4" height="6" rx="2" />
    <rect x="9" y="2" width="4" height="12" rx="2" />
  </svg>
);

const CaptionTape = () => (
  <div className="overflow-hidden py-5" aria-hidden="true">
    <div className="-rotate-1 scale-x-105 bg-stone-900 dark:ring-1 dark:ring-stone-800 py-3.5">
      {/* Two identical runs; the track slides exactly one run, so it loops seamlessly */}
      <div className="cap-tape-track flex w-max">
        {[0, 1].map((run) => (
          <div key={run} className="flex items-center gap-8 pr-8 shrink-0">
            {TAPE_CAPTIONS.map(({ text, style }) => (
              <span key={text} className="flex items-center gap-8 text-xl sm:text-2xl whitespace-nowrap" style={style}>
                {text}
                <Waveform />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* Dark stage every feature demo sits on, so the three cards line up */
const STAGE = 'h-40 rounded-2xl bg-stone-900 dark:ring-1 dark:ring-stone-700 flex flex-col items-center justify-center px-4 overflow-hidden';

/* ─── Karaoke demo line: shows word-level sync by doing it ── */
const KaraokeLine = () => {
  const words = ['toh', 'chaliye', 'shuru', 'karte', 'hain'];
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % words.length), 480);
    return () => clearInterval(id);
  }, [words.length]);
  return (
    <div className={STAGE}>
      <div className="flex flex-wrap justify-center items-center gap-x-2 gap-y-1">
        {words.map((w, i) => (
          <span
            key={w}
            className={`inline-block font-brand font-extrabold text-xl transition-all duration-200 ${i === active ? 'text-marigold-400 scale-110' : 'text-stone-500'}`}
          >
            {w}
          </span>
        ))}
      </div>
      {/* Word timings as clips on a track: the lit clip is the word being spoken */}
      <div className="flex gap-1 mt-5 w-full max-w-[220px]">
        {[3, 7, 5, 5, 4].map((w, i) => (
          <span key={i} style={{ flexGrow: w }} className={`h-1.5 rounded-full transition-colors duration-200 ${i === active ? 'bg-marigold-400' : 'bg-stone-700'}`} />
        ))}
      </div>
    </div>
  );
};

/* ─── Template cycler: the same caption line restyled live ── */
const TEMPLATE_LOOKS = [
  { name: 'Hormozi', style: { fontFamily: 'Oswald, sans-serif', fontWeight: 700, color: '#8ABE4C', textTransform: 'uppercase' } },
  { name: 'MrBeast', style: { fontFamily: 'Bangers, cursive', color: '#FF5252', letterSpacing: '1px', textTransform: 'uppercase' } },
  { name: 'Handwritten', style: { fontFamily: 'Kalam, cursive', fontWeight: 700, color: '#F7C95C' } },
  { name: 'Desi Word', style: { fontFamily: 'Montserrat, sans-serif', color: '#FFFFFF', fontWeight: 800 } },
];

const TemplateCycler = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((v) => (v + 1) % TEMPLATE_LOOKS.length), 1800);
    return () => clearInterval(id);
  }, []);
  return (
    <div className={STAGE}>
      <Motion.span
        key={i}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="block text-3xl whitespace-nowrap"
        style={TEMPLATE_LOOKS[i].style}
      >
        kya baat hai!
      </Motion.span>
      <span className="mt-4 text-xs font-jakarta font-semibold text-stone-400">{TEMPLATE_LOOKS[i].name}</span>
    </div>
  );
};

/* ─── Export stage: what one upload hands back ── */
const ExportFiles = () => (
  <div className={`${STAGE} !items-stretch gap-1.5 font-jetbrains text-xs`}>
    {[
      ['movie', 'reel_final.mp4', 'captions burned in', true],
      ['subtitles', 'reel_final.srt', 'CapCut, Premiere', false],
      ['subtitles', 'reel_final.vtt', 'YouTube Studio', false],
      ['subtitles', 'reel_final.ass', 'styled, for Resolve', false],
    ].map(([icon, file, note, main]) => (
      <div key={file} className="flex items-center gap-2.5">
        <Icon name={icon} className={`text-[16px] ${main ? 'text-marigold-400' : 'text-stone-500'}`} />
        <span className={main ? 'text-marigold-400' : 'text-stone-300'}>{file}</span>
        <span className="ml-auto text-stone-500 truncate">{note}</span>
      </div>
    ))}
  </div>
);

/* ─── The pitch in two lines: what every other tool hears, and what we write ──
   `true` marks a misheard token, so the squiggle lands where the damage is. */
const MISHEARD = [['toe', true], ['charlie', true], ['shuru', false], ['cutting', true], ['high', true]];

const HinglishProof = () => {
  const LINE = 'flex items-baseline gap-3 sm:gap-4 font-jakarta text-2xl sm:text-[2rem] leading-snug';
  return (
    <div className="mt-10 lg:mt-12 space-y-5">
      <p className={`${LINE} text-stone-400 dark:text-stone-600`}>
        <Icon name="close" className="text-[20px] shrink-0 text-red-400" />
        <span>
          {MISHEARD.map(([word, bad], i) => (
            <span key={i} className={bad ? '[text-decoration:underline_wavy_#f87171] [text-underline-offset:6px] [text-decoration-skip-ink:none]' : ''}>{word}{' '}</span>
          ))}
        </span>
      </p>
      <p className={`${LINE} font-bold text-stone-900 dark:text-stone-100`}>
        <Icon name="check" className="text-[20px] shrink-0 text-indigo-600 dark:text-indigo-400" />
        <span>toh chaliye shuru karte <span className="rounded-lg bg-marigold-400 px-1.5 text-stone-900">hain</span></span>
      </p>
    </div>
  );
};

/* ─── Features ───────────────────────────────────────────── */
const Features = () => (
  <section id="features" className="py-20 sm:py-28 px-4 sm:px-6">
    <div className="max-w-7xl mx-auto">
      <div className="max-w-3xl">
        <h2 className={`${H2} mb-5`}>
          Hindi mein bolo. Hinglish mein dikhao.
        </h2>
        <p className={`${LEAD} max-w-2xl`}>DesiCaptions has one job: captions that read the way your audience actually texts.</p>
      </div>

      <HinglishProof />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-14 lg:mt-20">
        {/* Templates: one line of caption, restyled live */}
        <div className="flex flex-col">
          <TemplateCycler />
          <div className="pt-5">
            <h3 className="text-2xl font-brand font-extrabold leading-tight mb-2 text-stone-900 dark:text-stone-100">{TEMPLATES.length} styles that already work</h3>
            <p className="text-[15px] text-stone-600 dark:text-stone-400 font-jakarta leading-relaxed">Hormozi, MrBeast, Ali Abdaal and desi originals, all tuned for Hindi text. One click restyles the whole video.</p>
          </div>
        </div>

        {/* Word-level sync */}
        <div className="flex flex-col">
          <KaraokeLine />
          <div className="pt-5">
            <h3 className="text-2xl font-brand font-extrabold leading-tight mb-2 text-stone-900 dark:text-stone-100">Every word lands on beat</h3>
            <p className="text-[15px] text-stone-600 dark:text-stone-400 font-jakarta leading-relaxed">Each word is timed to the millisecond it's spoken. The highlight moves with your voice, never two seconds behind it.</p>
          </div>
        </div>

        {/* Burn or Download */}
        <div className="flex flex-col">
          <ExportFiles />
          <div className="pt-5">
            <h3 className="text-2xl font-brand font-extrabold leading-tight mb-2 text-stone-900 dark:text-stone-100">One upload, posts anywhere</h3>
            <p className="text-[15px] text-stone-600 dark:text-stone-400 font-jakarta leading-relaxed">Burn captions straight into the video for Reels and Shorts, or hand the subtitle files to your editor of choice.</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ─── New styles: one whole video, a different template on every sentence ──
   public/templates/showcase.mp4 is burned by scripts/render-template-clips.mjs showcase through
   the same pipeline as a user export: the full source clip, start to finish, with the caption
   style switching at sentence boundaries. src/data/showcaseWindows.json says which style is on
   screen when, so the list below follows the playhead and a click seeks to that style. */
const STYLE_BLURBS = {
  editorial: 'Bold caps with one serif italic word mixed in. Words blur in as you speak.',
  handwritten: 'Marker handwriting, with the key word brush-lettered on its own line.',
  poster: 'Flowing script over one giant red word, like a film poster.',
  typewriter: 'Types itself out letter by letter, in time with your voice.',
  stagger: 'Lines step down like a staircase, and the key word lands big in yellow.',
  'pop-off': 'One word at a time, anywhere on the frame. Drag any word to move it.',
};
const NEW_STYLES = SHOWCASE_WINDOWS.map((w) => ({
  ...w,
  name: TEMPLATES.find((t) => t.id === w.id)?.name || w.name || w.id,
  blurb: STYLE_BLURBS[w.id] || '',
}));

const NewTag = ({ className = '' }) => (
  <span className={`inline-block px-2.5 py-0.5 bg-marigold-400 text-stone-900 border-2 border-stone-900 text-xs font-bold rounded-full font-jakarta -rotate-2 ${className}`}>New</span>
);

const windowAt = (t) => {
  const i = NEW_STYLES.findIndex((w) => t < w.end);
  return i < 0 ? NEW_STYLES.length - 1 : i;
};

const NewStyles = () => {
  const { user } = useAuth();
  const reduceMotion = useReducedMotion();
  const stageRef = useRef(null);
  const videoRef = useRef(null);
  const rafRef = useRef(0);
  /* Which style is on screen and how far through its window the playhead is. */
  const [pos, setPos] = useState({ index: 0, frac: 0 });
  const [onScreen, setOnScreen] = useState(false);
  /* The stage starts muted, because autoplay anywhere else is blocked and rude. Clicking the
     clip toggles sound so the lines being captioned can be heard. */
  const [sound, setSound] = useState(false);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const io = new IntersectionObserver(([entry]) => setOnScreen(entry.isIntersecting), { threshold: 0.25 });
    io.observe(stage);
    return () => io.disconnect();
  }, []);

  /* Play only while in view. Reduced motion leaves the clip on its poster with native controls. */
  useEffect(() => {
    const v = videoRef.current;
    if (!v || reduceMotion) return;
    if (onScreen) v.play().catch(() => { });
    else v.pause();
  }, [onScreen, reduceMotion]);

  /* Follow the playhead: the list lights the style on screen and its bar fills across its
     window. One frame loop while in view, nothing when the section is off screen. */
  useEffect(() => {
    if (!onScreen) return undefined;
    const tick = () => {
      const v = videoRef.current;
      if (v) {
        const t = v.currentTime;
        const index = windowAt(t);
        const w = NEW_STYLES[index];
        const frac = Math.max(0, Math.min(1, (t - w.start) / Math.max(0.001, w.end - w.start)));
        setPos((p) => (p.index === index && Math.abs(p.frac - frac) < 0.01 ? p : { index, frac }));
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [onScreen]);

  const active = NEW_STYLES[pos.index];
  const pick = (i) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = NEW_STYLES[i].start + 0.01;
    setPos({ index: i, frac: 0 });
    if (!reduceMotion) v.play().catch(() => { });
  };

  return (
    <section id="new-styles" className="py-14 sm:py-20">
      {/* Narrower than the rest of the page and centred: at full width the clip sat on one edge
          and the track ran to the other, with the text stretched across the gap. */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {/* The monitor keeps its own column so the heading has something to stand beside, rather
            than a tall 9:16 clip floating under a full-width block of text. Stacked on a phone it
            takes explicit grid slots instead, so the heading introduces the clip before it plays. */}
        <div ref={stageRef} className="grid grid-cols-1 gap-8 lg:gap-x-14 lg:gap-y-8 lg:grid-cols-[minmax(0,300px)_minmax(0,1fr)]">
          <div className="lg:col-start-2 lg:row-start-1">
            <NewTag className="mb-4" />
            <h2 className={`${H2} mb-5`}>Four new ways to caption.</h2>
            <p className={`${LEAD} max-w-xl`}>One video, six styles. Every frame is a real export, not a mockup.</p>
          </div>

          <div className="cap-card rounded-2xl w-full max-w-[260px] sm:max-w-[300px] mx-auto lg:mx-0 self-start lg:self-center lg:col-start-1 lg:row-start-1 lg:row-span-2">
            <div className="relative aspect-[9/16] rounded-[inherit] bg-stone-900 overflow-hidden">
              <video
                ref={videoRef}
                src="/templates/showcase.mp4"
                poster="/templates/showcase.jpg"
                className="absolute inset-0 w-full h-full object-cover"
                muted={!sound}
                loop
                playsInline
                preload="metadata"
                controls={reduceMotion}
                aria-label="Six caption styles on one sample video"
              />
              {/* The whole clip is the mute toggle, with the hero's own control as its affordance.
                  Reduced motion gets native controls instead, which carry their own volume. */}
              {!reduceMotion && (
                <button
                  type="button"
                  onClick={() => setSound((on) => !on)}
                  aria-pressed={sound}
                  aria-label={sound ? 'Mute the preview' : 'Unmute the preview'}
                  className="group absolute inset-0 z-20 flex items-start justify-end p-2.5 focus-visible:outline-3 focus-visible:outline-marigold-400 focus-visible:-outline-offset-3"
                >
                  <span className="w-8 h-8 rounded-full bg-black/55 backdrop-blur-md border border-white/10 flex items-center justify-center text-white/90 group-hover:bg-black/70 transition-colors">
                    {sound ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  </span>
                </button>
              )}
            </div>
          </div>

          <div className="lg:col-start-2 lg:row-start-2">
            {/* Each style is a clip on a track, in the order it plays; the one on screen fills
                as its sentence runs. Picking one jumps the video there. */}
            <ul className="max-w-xl">
              {NEW_STYLES.map((t, i) => {
                const on = i === pos.index;
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => pick(i)}
                      aria-current={on ? 'true' : undefined}
                      className="group flex w-full items-center gap-4 sm:gap-5 py-2.5 text-left focus-visible:outline-3 focus-visible:outline-marigold-400 focus-visible:outline-offset-2"
                    >
                      <span className={`font-brand font-extrabold text-lg sm:text-xl w-[6.5rem] sm:w-[8.5rem] shrink-0 transition-colors ${on
                        ? 'text-stone-900 dark:text-stone-100'
                        : 'text-stone-400 dark:text-stone-600 group-hover:text-stone-700 dark:group-hover:text-stone-300'}`}>
                        {t.name}
                      </span>
                      <span className="relative h-1.5 flex-1 rounded-full bg-stone-200/70 dark:bg-stone-800 overflow-hidden group-hover:bg-stone-300 dark:group-hover:bg-stone-700 transition-colors">
                        {on && (
                          <span
                            className="absolute inset-y-0 left-0 rounded-full bg-marigold-400"
                            style={{ width: `${Math.round(pos.frac * 100)}%` }}
                          />
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Height reserved, so advancing the track never nudges the button. */}
            <p className="mt-4 max-w-xl min-h-[3rem] sm:min-h-[1.75rem] font-jakarta text-[15px] text-stone-600 dark:text-stone-400" aria-live="polite">{active.blurb}</p>

            <Link to={user ? '/app' : '/register'} className={`${BTN_PRIMARY} mt-5 px-7 py-3.5 text-base`}>
              Try them on your video
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── How It Works ───────────────────────────────────────── */
const HowItWorks = () => {
  const steps = [
    { title: 'Upload', desc: 'Drop any MP4, MOV, MP3 or M4A. Hindi, English or Hinglish, it handles all three.', time: 'YOUR VIDEO' },
    { title: 'Transcribe', desc: 'AI writes every spoken word in Roman script or the native script, your pick. No gibberish.', time: 'AI FIRST DRAFT' },
    { title: 'Style', desc: 'Pick a template. Adjust font, color and position. See it live before exporting.', time: 'YOUR STYLE' },
    { title: 'Export', desc: 'Burn captions into the video, or download SRT and VTT for CapCut, Premiere or YouTube Studio.', time: 'READY TO SHARE' },
  ];

  return (
    <section id="how-it-works" className="py-20 sm:py-28 px-4 sm:px-6 bg-cream-100 dark:bg-stone-900/40">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12 lg:mb-16 max-w-3xl">
          <h2 className={`${H2} mb-5`}>Upload. Caption. Post.</h2>
          <p className={LEAD}>From your camera roll to your next post. Generate a draft, make it yours, and export.</p>
        </div>

        {/* Laid out like clips on an editor timeline: each step hangs off the
            same rule and carries its own duration. */}
        <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
          {steps.map((s, i) => (
            <li key={s.title} className="border-t-[3px] border-stone-900 dark:border-stone-100 pt-5">
              <div className="flex items-baseline justify-between mb-4">
                <span className="font-brand font-extrabold text-6xl leading-none text-indigo-600 dark:text-indigo-400">{i + 1}</span>
                <span className="font-jetbrains text-xs text-stone-500 dark:text-stone-400">{s.time}</span>
              </div>
              {/* h3 (not h4): section heading is an h2, skipping to h4 fails the
                  heading-order accessibility audit */}
              <h3 className="text-2xl font-brand font-extrabold mb-2 text-stone-900 dark:text-stone-100">{s.title}</h3>
              <p className="text-stone-600 dark:text-stone-400 font-jakarta text-[15px] leading-relaxed">{s.desc}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

/* ─── Platform: the transcript editor, shown instead of told ── */
const PlatformSection = () => (
  <section id="platform" className="py-20 sm:py-28 px-4 sm:px-6">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
      {/* Copy + control points */}
      <div>
        <h2 className={`${H2} mb-5`}>
          <span className="block">AI writes the draft.</span> You get final cut.
        </h2>
        <p className={`${LEAD} mb-10`}>
          Every video opens in a full transcript editor before you export. Nothing is locked, and nothing needs a second tool.
        </p>
        <ul className="space-y-6">
          {[
            { icon: 'edit', title: 'Fix any word in one click', desc: 'Tap a word to correct its spelling or nudge its timing. The preview updates instantly.' },
            { icon: 'verified_user', title: 'Devanagari guard built in', desc: 'If the AI slips out of Roman script, it catches the mistake and retries on its own.' },
            { icon: 'style', title: 'Restyle after the fact', desc: 'Swap templates, fonts, colors and position on the finished transcript, then export again.' },
          ].map(({ icon, title, desc }) => (
            <li key={title} className="flex gap-4">
              <Icon name={icon} className="text-[24px] shrink-0 mt-0.5 text-indigo-600 dark:text-indigo-400" />
              <div>
                <h3 className="font-jakarta font-bold text-lg text-stone-900 dark:text-stone-100">{title}</h3>
                <p className="text-[15px] text-stone-600 dark:text-stone-400 font-jakarta leading-relaxed mt-0.5">{desc}</p>
              </div>
            </li>
          ))}
        </ul>
        <p className="mt-10 text-xs font-jetbrains text-stone-500 dark:text-stone-500">MP4, MOV, MKV, WEBM, MP3, M4A, WAV. Files up to 4 GB.</p>
      </div>

      {/* Editor mock: a word being corrected mid-transcript */}
      <div className="cap-card [--cap-shadow:#4F46E5] rounded-3xl bg-stone-900 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-stone-800">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-stone-700" />
            <span className="w-2.5 h-2.5 rounded-full bg-stone-700" />
            <span className="w-2.5 h-2.5 rounded-full bg-stone-700" />
          </div>
          <span className="font-jakarta text-xs text-stone-500">Transcript editor</span>
          <span className="font-jakarta text-xs text-emerald-400 flex items-center gap-1"><Icon name="check" className="text-[14px]" />Saved</span>
        </div>
        <div className="p-6 space-y-4 font-jakarta text-base">
          <div className="flex gap-4 items-baseline text-stone-500">
            <span className="font-jetbrains text-[11px] text-stone-600 shrink-0">00:04.2</span>
            <span>toh chaliye shuru karte hain</span>
          </div>
          <div className="flex gap-4 items-baseline rounded-xl bg-stone-800/60 px-3 py-2.5 ring-1 ring-stone-700">
            <span className="font-jetbrains text-[11px] text-indigo-300 shrink-0">00:06.8</span>
            <span className="text-stone-200 leading-relaxed">
              aaj main aapko <span className="text-stone-500 line-through decoration-red-400/70">btaunga</span>{' '}
              <span className="text-stone-900 bg-marigold-400 rounded px-1 whitespace-nowrap">bataunga<span className="inline-block w-px h-4 bg-stone-900 ml-0.5 align-middle animate-pulse" /></span> ki
            </span>
          </div>
          <div className="flex gap-4 items-baseline text-stone-500">
            <span className="font-jetbrains text-[11px] text-stone-600 shrink-0">00:09.1</span>
            <span>views kaise double karein</span>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-stone-800 bg-stone-950/40">
          <div className="flex gap-2">
            <span className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white font-jakarta text-xs font-bold">Re-export</span>
            <span className="px-3 py-1.5 rounded-lg bg-stone-800 text-stone-300 font-jakarta text-xs font-semibold">Change style</span>
          </div>
          <span className="font-jetbrains text-[11px] text-stone-500">word 14 of 212</span>
        </div>
      </div>
    </div>
  </section>
);

/* ─── Solutions ──────────────────────────────────────────── */
const SolutionsSection = () => (
  <section id="solutions" className="py-20 sm:py-28 px-4 sm:px-6 bg-cream-100 dark:bg-stone-900/40">
    <div className="max-w-7xl mx-auto">
      <div className="mb-12 lg:mb-16 max-w-3xl">
        <h2 className={`${H2} mb-5`}>Who is this actually for?</h2>
        <p className={LEAD}>
          If you make Hindi or Hinglish content and care about retention, this was made for you.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        {/* Creators */}
        <div className={`${CARD} p-7 sm:p-10`}>
          <h3 className="text-3xl font-brand font-extrabold mb-4 text-stone-900 dark:text-stone-100">YouTube & Reels creators</h3>
          <p className="text-stone-600 dark:text-stone-400 mb-8 leading-relaxed font-jakarta text-[17px]">
            You record in Hinglish. CapCut auto-captions produce garbage. You spend 30 minutes manually fixing every subtitle. DesiCaptions generates clean Roman-script captions in one upload: pick a style, burn it in, post it.
          </p>
          <ul className="space-y-3.5 text-stone-700 dark:text-stone-300">
            <Check>No more manually timing each line</Check>
            <Check>{TEMPLATES.length} viral templates, Hormozi and MrBeast styles included</Check>
            <Check>Ready to upload straight to Shorts or Reels</Check>
          </ul>
        </div>

        {/* Agencies */}
        <div className="rounded-3xl p-7 sm:p-10 bg-stone-900 dark:ring-1 dark:ring-stone-700">
          <h3 className="text-3xl font-brand font-extrabold mb-4 text-white">Editors & agencies</h3>
          <p className="text-stone-400 mb-8 leading-relaxed font-jakarta text-[17px]">
            You handle 20+ videos a month for clients. You need accurate subtitles fast. Process an entire batch in the time it used to take one video, and hand off SRT files directly to CapCut or Premiere.
          </p>
          <ul className="space-y-3.5 text-stone-200">
            <Check color="text-marigold-400">SRT / VTT / ASS for any editing software</Check>
            <Check color="text-marigold-400">Greenscreen export for custom overlays</Check>
            <Check color="text-marigold-400">Up to 12 hours of video with the Agency pack</Check>
          </ul>
        </div>
      </div>
    </div>
  </section>
);

/* ─── Pricing ────────────────────────────────────────────── */
// The free tier is the only card defined here; paid packs (prices, minutes,
// currency) come from the backend catalogue via usePricing so there is a single
// source of truth. Material icon names per pack id, cheapest first.
const PACK_ICONS = {
  mini: 'bolt', // retired from sale; kept so a legacy catalogue still renders
  starter: 'diamond',
  growth: 'rocket_launch',
  agency: 'crown',
};

// Even a zero price has to carry the buyer's currency symbol: this card renders
// beside catalogue packs, and a "$0" sitting next to "₹799" reads as a second
// storefront. Mirrors CURRENCY_META in backend/src/config/pricing.js; an
// unknown currency falls back to the dollar sign the catalogue defaults to.
const CURRENCY_SYMBOLS = { USD: '$', INR: '₹' };

const freePlanFor = (currency) => ({
  id: 'free', title: 'Free', price: `${CURRENCY_SYMBOLS[currency] || '$'}0`, period: 'forever',
  videos: '2 free minutes', videoNote: 'videos up to 5 min', icon: 'link', isPopular: false,
  cta: { label: 'Get Started Free', href: '/register' },
  features: ['2 free minutes of subtitles', 'All subtitle templates', 'SRT / VTT / ASS export'],
});

/** Shapes a backend catalogue pack into the props PricingCard expects. */
const toPricingCard = (pack) => ({
  id: pack.id,
  title: pack.name,
  price: pack.price,
  period: pack.period,
  videos: `${pack.minutes} minutes`,
  videoNote: `${pack.perMinute}/min`,
  icon: PACK_ICONS[pack.id] || 'diamond',
  isPopular: pack.popular,
  cta: { label: `Buy ${pack.name}` },
  features: pack.features,
});

const PricingCard = ({ plan, onCheckout, isLoading }) => {
  const { title, price, period, videos, videoNote, isPopular, cta, features } = plan;
  const ctaCls = isPopular
    ? `${BTN_PRIMARY} w-full py-3.5 px-6 text-[15px] disabled:cursor-not-allowed`
    : 'w-full py-3.5 px-6 rounded-2xl font-jakarta text-[15px] font-bold text-center block border-2 border-stone-900 dark:border-stone-100 text-stone-900 dark:text-stone-100 hover:bg-stone-900 hover:text-white dark:hover:bg-stone-100 dark:hover:text-stone-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className={`p-7 rounded-3xl relative flex flex-col bg-white dark:bg-stone-900 ${isPopular ? 'cap-card [--cap-shadow:#4F46E5]' : 'border border-stone-200 dark:border-stone-800'}`}>
      {isPopular && (
        <div className="absolute -top-3.5 left-7 px-3 py-1 bg-marigold-400 text-stone-900 border-2 border-stone-900 text-xs font-bold rounded-full font-jakarta -rotate-2">
          Most popular
        </div>
      )}

      <h3 className="text-2xl font-extrabold text-stone-900 dark:text-stone-100 font-brand mb-3">{title}</h3>
      <div className="flex items-baseline gap-1.5 mb-3">
        <span className="text-5xl font-extrabold leading-none text-stone-900 dark:text-stone-100 font-brand">{price}</span>
        <span className="text-[15px] text-stone-500 dark:text-stone-400 font-jakarta">/ {period}</span>
      </div>
      <p className="mb-7 font-jakarta text-[15px]">
        <span className="font-bold text-stone-900 dark:text-stone-100">{videos}</span>
        <span className="text-stone-500 dark:text-stone-400">, {videoNote}</span>
      </p>
      <div className="mb-7">
        {plan.id === 'free' ? (
          <Link to={cta.href} className={ctaCls}>
            {cta.label}
          </Link>
        ) : (
          <button onClick={() => onCheckout(plan.id)} disabled={isLoading} className={ctaCls}>
            {isLoading ? 'Loading...' : cta.label}
          </button>
        )}
      </div>
      <ul className="flex-1 flex flex-col gap-3 border-t border-stone-200 dark:border-stone-800 pt-6 text-stone-700 dark:text-stone-300">
        {features.map((f) => (
          <Check key={f} color={isPopular ? 'text-indigo-600 dark:text-indigo-400' : 'text-stone-400 dark:text-stone-500'}>{f}</Check>
        ))}
      </ul>
    </div>
  );
};

const PricingSection = () => {
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const { user, token } = useAuth(); // token will be used to pass auth explicitly
  const { packs, currency, loading: pricingLoading } = usePricing();
  const plans = [freePlanFor(currency), ...packs.map(toPricingCard)];

  const handleJoinWaitlist = (planId) => {
    setSelectedPlanId(planId);
    setShowWaitlist(true);
  };

  const handleCheckout = async (planId) => {
    if (!user) {
      localStorage.setItem('pendingPlanCheckout', planId);
      window.location.href = '/register';
      return;
    }

    try {
      setLoadingPlan(planId);
      const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` // use the explicit token
        },
        body: JSON.stringify({ planId, currency })
      });

      const data = await res.json();
      if (res.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(data.error || 'Failed to initialize checkout');
      }
    } catch (err) {
      console.error(err);
      toast.error('Network error while initializing checkout.');
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <section id="pricing" className="py-20 sm:py-28 px-4 sm:px-6 bg-cream-100 dark:bg-stone-900/40">
      <div className="max-w-7xl mx-auto">
        <div className="mb-14 lg:mb-16 max-w-3xl">
          <h2 className={`${H2} mb-5`}>Start free. Scale when ready.</h2>
          <p className={LEAD}>
            Buy minutes once, use them anytime. No subscription, no surprise charges. Your minutes never expire.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
          {/* Prices come from the server, so skeletons hold the layout rather
              than flashing a placeholder price the checkout might not match. */}
          {pricingLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={`skeleton-${i}`}
                  className="p-7 rounded-3xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 animate-pulse"
                  aria-hidden="true"
                >
                  <div className="h-6 w-24 rounded bg-stone-100 dark:bg-stone-800 mb-4" />
                  <div className="h-11 w-28 rounded bg-stone-100 dark:bg-stone-800 mb-4" />
                  <div className="h-5 w-36 rounded bg-stone-100 dark:bg-stone-800 mb-7" />
                  <div className="h-12 w-full rounded-2xl bg-stone-100 dark:bg-stone-800 mb-7" />
                  <div className="flex flex-col gap-3 border-t border-stone-200 dark:border-stone-800 pt-6">
                    {Array.from({ length: 4 }).map((__, j) => (
                      <div key={j} className="h-4 w-full rounded bg-stone-100 dark:bg-stone-800" />
                    ))}
                  </div>
                </div>
              ))
            : plans.map((plan) => (
            <PricingCard
              key={plan.id}
              plan={plan}
              onJoinWaitlist={handleJoinWaitlist}
              onCheckout={handleCheckout}
              isLoading={loadingPlan === plan.id}
            />
          ))}
        </div>
        <p className="text-[15px] text-stone-600 dark:text-stone-400 font-jakarta mt-12 leading-relaxed">
          All prices in {currency}. One-time payment, no subscription.{' '}
          <a href="mailto:hello@desicaptions.com" className="underline underline-offset-4 decoration-stone-300 dark:decoration-stone-600 hover:text-stone-900 dark:hover:text-stone-100 transition-colors">Questions? We reply within 24h</a>
        </p>
      </div>

      {showWaitlist && (
        <WaitlistModal
          source="pricing_page"
          planId={selectedPlanId}
          onClose={() => setShowWaitlist(false)}
        />
      )}
    </section>
  );
};

/* ─── FAQ ────────────────────────────────────────────────── */
const FAQ_ITEMS = [
  {
    q: 'Does it work for pure Hindi, or only Hinglish?',
    a: 'Both. Whether your video is 100% Hindi, 100% English, or any mix of the two, the AI handles it. Pick Hinglish for Roman-script captions or Hindi for Devanagari, per video.',
  },
  {
    q: 'What file formats can I upload?',
    a: 'Video: MP4, MOV, MKV, WebM. Audio: MP3, M4A, WAV. Maximum file size is 4 GB. Videos can be up to 5 minutes long, or up to 15 minutes on the Agency pack. Each video uses exactly its own length from your balance, down to the second.',
  },
  {
    q: 'How long does processing actually take?',
    a: 'A 5-minute video typically takes 2 to 3 minutes from upload to ready, including word-level sync against the audio. A 15-minute video on the Agency pack takes around 5 to 6 minutes.',
  },
  {
    q: "What's the difference between 'burn-in' and 'sidecar' export?",
    a: "Burn-in bakes the styled captions permanently into your video - perfect for uploading straight to Instagram Reels or YouTube Shorts. Sidecar is a separate SRT/VTT/ASS file you drag into CapCut, Premiere, DaVinci Resolve, or YouTube Studio.",
  },
  {
    q: 'Can I edit the captions after the AI generates them?',
    a: 'Yes. Every job has a full transcript editor where you can correct text, adjust timing, change styles, and re-export. Nothing is locked - you have complete control.',
  },
];

const FAQSection = () => {
  const [open, setOpen] = useState(null);
  return (
    <section className="py-20 sm:py-28 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-[minmax(0,4fr)_minmax(0,7fr)] gap-10 lg:gap-16">
        <div>
          <h2 className={`${H2} mb-5`}>Common questions</h2>
          <p className={LEAD}>Everything you need to know before you upload your first video.</p>
        </div>
        <div className="border-b border-stone-300 dark:border-stone-700">
          {FAQ_ITEMS.map((item, i) => (
            <div key={item.q} className="border-t border-stone-300 dark:border-stone-700">
              <button
                className="w-full text-left py-5 sm:py-6 flex items-start justify-between gap-6 group"
                onClick={() => setOpen(open === i ? null : i)}
                aria-expanded={open === i}
              >
                <span className="font-bold font-jakarta text-stone-900 dark:text-stone-100 text-lg leading-snug group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">{item.q}</span>
                <Icon name="add" className={`shrink-0 text-2xl text-stone-900 dark:text-stone-100 transition-transform duration-200 ${open === i ? 'rotate-45' : ''}`} />
              </button>
              {open === i && (
                <p className="pb-6 pr-10 text-stone-600 dark:text-stone-300 font-jakarta text-base leading-relaxed max-w-2xl">{item.a}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── CTA ────────────────────────────────────────────────── */
const CTA = () => (
  <section className="pb-20 sm:pb-28 px-4 sm:px-6">
    <div className="max-w-7xl mx-auto">
      <div className="landing-final-cta cap-card bg-stone-900 rounded-[2rem] px-6 py-14 sm:p-16 lg:p-20">
        <div className="max-w-3xl">
          <h2 className="font-brand font-extrabold text-white text-[2.75rem] sm:text-6xl lg:text-7xl leading-[1.02] mb-6">
            You bring the story. We’ll bring the captions.
          </h2>
          <p className="text-stone-300 text-lg lg:text-xl mb-10 font-jakarta leading-relaxed max-w-2xl">
            Upload a real video right now. See exactly what your captions look like before committing to anything.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <Link
              to="/register"
              className="cap-btn [--cap-ink:#1C1917] inline-flex items-center justify-center px-9 py-4 bg-marigold-400 text-stone-900 rounded-2xl font-jakarta text-[17px] font-bold"
            >
              Try it free
            </Link>
            <a
              href="#pricing"
              className="inline-flex items-center justify-center px-4 py-4 text-white font-jakarta text-[17px] font-semibold underline underline-offset-4 decoration-white/40 hover:decoration-white transition-colors"
            >
              View pricing
            </a>
          </div>
          <p className="mt-8 text-stone-300 font-jakarta text-[15px]">First 2 minutes free. No credit card, no subscription, full-quality export.</p>
        </div>
      </div>
    </div>
  </section>
);

/* ─── Footer ─────────────────────────────────────────────── */
const Footer = () => (
  <footer className="bg-white dark:bg-stone-900 border-t border-stone-200 dark:border-stone-800 pt-16 pb-10">
    <div className="max-w-7xl mx-auto px-6">
      <div className="flex flex-col md:flex-row justify-between gap-10 mb-12">
        <div className="max-w-xs">
          <div className="flex items-center gap-2.5 mb-4">
            <Logo className="w-7 h-7" />
            <span className="text-xl font-extrabold font-brand text-stone-900 dark:text-stone-100">DesiCaptions</span>
          </div>
          <p className="text-sm text-stone-500 dark:text-stone-400 leading-relaxed font-jakarta">
            AI-powered Hinglish subtitles for Indian creators. Upload, caption, and post in minutes.
          </p>

        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
          {[
            { heading: 'Product', links: [{ name: 'Features', href: '#features' }, { name: 'Pricing', href: '#pricing' }, { name: 'How it works', href: '#how-it-works' }] },
            /* Free tools are static prerendered pages (see scripts/generate-seo-pages.mjs),
               not SPA routes — absolute URLs to the production host, since these files
               only exist on www.desicaptions.com (any other host would 404 to the SPA). */
            {
              heading: 'Free Tools', links: [
                { name: 'Hinglish Subtitle Generator', href: 'https://www.desicaptions.com/hinglish-subtitle-generator' },
                { name: 'Hindi Subtitle Generator', href: 'https://www.desicaptions.com/hindi-subtitle-generator' },
                { name: 'Captions for Instagram Reels', href: 'https://www.desicaptions.com/add-captions-to-instagram-reels' },
                { name: 'YouTube Shorts Captions', href: 'https://www.desicaptions.com/youtube-shorts-caption-generator' },
                { name: 'SRT File Generator', href: 'https://www.desicaptions.com/srt-file-generator' },
              ]
            },
            // Blog is an href, not a path: it is statically generated into
            // public/blog and lives outside React Router, so a <Link> would
            // client-side route to the catch-all 404 instead of loading it.
            {
              heading: 'Company',
              links: [
                { name: 'About', path: '/about' },
                { name: 'Blog', href: 'https://www.desicaptions.com/blog' },
              ]
            },
            { heading: 'Legal', links: [{ name: 'Privacy', path: '/privacy' }, { name: 'Terms', path: '/terms' }] },
          ].map((col) => (
            <div key={col.heading}>
              {/* h3 (not h5): the preceding heading on the page is an h2, and
                  skipping levels fails the heading-order accessibility audit */}
              <h3 className="font-jakarta text-sm font-bold text-stone-900 dark:text-stone-100 mb-4">{col.heading}</h3>
              <ul className="space-y-3 text-sm text-stone-500 dark:text-stone-400 font-jakarta">
                {col.links.map((l) => (
                  <li key={l.name}>
                    {l.path
                      ? <Link to={l.path} className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">{l.name}</Link>
                      : <a href={l.href} className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">{l.name}</a>
                    }
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-stone-100 dark:border-stone-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-sm text-stone-500 dark:text-stone-400 font-jakarta">© 2026 DesiCaptions</p>
        <a href="mailto:hello@desicaptions.com" className="text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 font-jakarta transition-colors">
          hello@desicaptions.com
        </a>
      </div>
    </div>
  </footer>
);

/* ─── Sticky Mobile CTA Bar ──────────────────────────────── */
/* Persistent, thumb-reachable CTA on mobile only — a reliable conversion lift on long
   landing pages where most Indian creator traffic is on phones. Hidden on lg+.
   It slides in once the visitor scrolls past most of the first screen: on load it
   covered the hero's caption-style chips and repeated the hero's own CTA. */
const MobileCTABar = () => {
  const { user } = useAuth();
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const onScroll = () => setShown(window.scrollY > window.innerHeight * 0.8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div
      aria-hidden={!shown}
      className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 bg-white/90 dark:bg-stone-950/90 backdrop-blur-lg border-t border-stone-200/70 dark:border-stone-800/70 transition-transform duration-300 ${shown ? 'translate-y-0' : 'translate-y-full pointer-events-none'}`}
    >
      <div className="flex items-center gap-3 max-w-md mx-auto">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold font-jakarta text-stone-900 dark:text-stone-100 leading-tight truncate">Caption your first 2 minutes free</p>
          <p className="text-xs font-jakarta text-stone-500 dark:text-stone-400">No credit card needed</p>
        </div>
        <Link
          to={user ? '/app' : '/register'}
          tabIndex={shown ? undefined : -1}
          className="shrink-0 px-5 py-3 bg-indigo-600 text-white rounded-xl font-jakarta text-sm font-bold active:scale-95 transition-transform"
        >
          {user ? 'Dashboard' : 'Try Free'}
        </Link>
      </div>
    </div>
  );
};

/* ─── Page Root ──────────────────────────────────────────── */
const LandingPage = () => {
  // The navbar anchors (/#pricing etc.) also arrive from /login and /register.
  // On that fresh load the browser resolves the hash before React has rendered
  // the sections, so nothing scrolls — retry once the content is in the DOM.
  useEffect(() => {
    const { hash } = window.location;
    if (!hash) return;
    const id = requestAnimationFrame(() => {
      document.getElementById(hash.slice(1))?.scrollIntoView();
    });
    return () => cancelAnimationFrame(id);
  }, []);

  return (
  <div className="landing-page min-h-screen selection:bg-marigold-400 selection:text-stone-900 font-jakarta bg-cream-50 dark:bg-stone-950">
    {/* OG/Twitter tags live statically in index.html — social scrapers don't run JS,
        so Helmet-managed duplicates there never get seen and only drift out of sync. */}
    <Helmet>
      <title>DesiCaptions: AI Captions for Hindi, Hinglish and Indian Languages</title>
      <meta name="description" content="Upload your reel, get it back captioned in Hinglish or Hindi, ready to post. Word-synced subtitles, 23 caption templates, first 2 minutes free." />
      <link rel="canonical" href="https://www.desicaptions.com/" />
    </Helmet>
    <SiteHeader />
    <main>
      <Hero />
      <CaptionTape />
      <NewStyles />
      <Features />
      <HowItWorks />
      <PlatformSection />
      <SolutionsSection />
      <PricingSection />
      <FAQSection />
      <CTA />
    </main>
    <Footer />
    {/* Spacer so the sticky mobile CTA bar never covers footer content */}
    <div className="h-20 lg:hidden" />
    <MobileCTABar />
  </div>
  );
};

export default LandingPage;
